import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import reprocessamentoService from '../../reprocessamentoService';
import { EnumExecutionStatus, IPixJobStatus, JobType } from '../../types';
import {
  Card,
  CardTitle,
  JobIdRow,
  JobIdValue,
  CopyButton,
  StatusForm,
  InputGroup,
  ActionButton,
  StatusBadge,
  StatusDetail,
  ErrorMessage,
} from './styles';

interface PixJobSectionProps {
  startedJobId?: string | null;
  startedMessage?: string | null;
  /** Tipo do job — define qual endpoint de status será consultado. */
  jobType?: JobType;
  /** Título do card exibido ao iniciar o reprocessamento. */
  startedTitle?: string;
}

const statusLabels: Record<EnumExecutionStatus, string> = {
  [EnumExecutionStatus.Started]: 'Em processamento',
  [EnumExecutionStatus.Concluded]: 'Sucesso',
  [EnumExecutionStatus.Failed]: 'Erro',
};

const statusVariant: Record<EnumExecutionStatus, 'running' | 'success' | 'error'> = {
  [EnumExecutionStatus.Started]: 'running',
  [EnumExecutionStatus.Concluded]: 'success',
  [EnumExecutionStatus.Failed]: 'error',
};

const PixJobSection: React.FC<PixJobSectionProps> = ({
  startedJobId,
  startedMessage,
  jobType = JobType.Pix,
  startedTitle = 'Reprocessamento Pix iniciado',
}) => {
  const [jobId, setJobId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<IPixJobStatus | null>(null);

  useEffect(() => {
    if (startedJobId) {
      setJobId(startedJobId);
      setStatus(null);
    }
  }, [startedJobId]);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('JobId copiado!');
    } catch {
      toast.error('Não foi possível copiar o JobId.');
    }
  };

  const handleConsultar = async () => {
    const trimmed = jobId.trim();
    if (!trimmed) {
      toast.error('Informe o JobId para consultar.');
      return;
    }

    setLoading(true);
    try {
      const result = await reprocessamentoService.consultarStatus(jobType, trimmed);
      setStatus(result);
    } catch (error: any) {
      setStatus(null);
      if (error?.response?.status === 404) {
        toast.error(error.response.data?.message ?? 'Nenhum reprocessamento encontrado para o JobId informado.');
      } else {
        toast.error('Erro ao consultar o status do Job. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {startedJobId && (
        <Card>
          <CardTitle>{startedTitle}</CardTitle>
          {startedMessage && <p>{startedMessage}</p>}
          <JobIdRow>
            <span>JobId:</span>
            <JobIdValue>{startedJobId}</JobIdValue>
            <CopyButton type="button" onClick={() => handleCopy(startedJobId)}>
              Copiar
            </CopyButton>
          </JobIdRow>
        </Card>
      )}

      <Card>
        <CardTitle>Consulta de processamento</CardTitle>
        <StatusForm>
          <InputGroup>
            <label htmlFor="jobIdConsulta">JobId</label>
            <input
              id="jobIdConsulta"
              type="text"
              placeholder="JobId"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
            />
          </InputGroup>
          <ActionButton htmlType="button" onClick={handleConsultar} loading={loading}>
            Consultar
          </ActionButton>
        </StatusForm>

        {status && (
          <StatusDetail>
            <div>
              Status:{' '}
              <StatusBadge variant={statusVariant[status.status]}>
                {statusLabels[status.status]}
              </StatusBadge>
            </div>
            {/*
              No fluxo de transação o backend devolve o relatório em `result`
              (totalFound/sent/failures); no PIX só há `processedCount`, que
              o service normaliza a partir de result.sent.
            */}
            {status.result != null && (
              <div>Transações encontradas: {status.result.totalFound}</div>
            )}
            {status.processedCount != null && (
              <div>Transações sincronizadas: {status.processedCount}</div>
            )}
            {status.result != null && status.result.failures.length > 0 && (
              <div>Falhas: {status.result.failures.length}</div>
            )}
            {status.elapsedSeconds != null && (
              <div>Tempo decorrido: {status.elapsedSeconds}s</div>
            )}
            {status.isFailed && status.errorMessage && (
              <ErrorMessage>{status.errorMessage}</ErrorMessage>
            )}
          </StatusDetail>
        )}
      </Card>
    </>
  );
};

export default PixJobSection;
